import React, { useCallback, useMemo, useRef, useState } from 'react';

import { JourneyMenu } from 'src/common/JourneyMenu';
import { functionNoop } from 'src/utils/function/noop';
import ManagementDropdownTabs, { ManagementDropdownTabType } from './tabs';
import ManagementDropdownOrganizationList from './organization-list';
import { ChevronDownSmallIcon } from 'src/monet/icons';
import { useCurrentOrganization } from 'src/store/organization';
import ManagementDropdownWorkspaceList from './workspace-list';
import CreateOrganizationSlideover from '../../slide-over/create-organization.slideover';
import OrganizationLink from 'src/common/OrganizationLink';
import { ImageRenderer } from 'src/common/ImageRenderer';
import { generateInitials, generateFirstLetter, getWordWithCount } from 'src/utils/text';
import { useCurrentUser } from 'src/store/user';
import { EventBus } from 'src/common/EventBus';
import { OrganizationUser } from 'src/common/interfaces/organization/user.interface';
import { Nullable } from 'src/types/nullable.type';
import ManagementDropdownReferralsTab from './referrals-tab';

const ManagementDropdown = () => {
  const currentWorkspace = useCurrentOrganization((state) => state.currentWorkspace);
  const currentOrganization = useCurrentOrganization((state) => state.currentOrganization);
  const user: Nullable<OrganizationUser> = useCurrentUser((state) => state.currentUser);

  const userInitials = useMemo(() => (user ? generateInitials(user?.name) : null), [user]);
  const orgInitials = useMemo(
    () => generateFirstLetter(currentOrganization.name || currentOrganization.slug),
    [currentOrganization]
  );
  const [selectedTab, setSelectedTab] = useState<ManagementDropdownTabType>('workspaces');
  const [isCreatingOrganization, setIsCreatingOrganization] = useState(false);
  const settingsOpenRef = useRef(functionNoop);
  const settingsCloseRef = useRef(functionNoop);

  const onClose = useCallback(() => {
    settingsCloseRef.current();
  }, []);

  const handleSettingsClick = useCallback(() => {
    settingsOpenRef.current();
  }, []);

  const onChangeTab = (newValue: ManagementDropdownTabType) => {
    setSelectedTab(newValue);
  };

  const onCreateNewOrganizationClicked = () => {
    settingsCloseRef.current();
    setIsCreatingOrganization(true);
  };

  const handleCreateOrganizationSlideoverClose = () => {
    setIsCreatingOrganization(false);
  };

  const onWorkspaceChanged = () => {
    settingsCloseRef.current();
    EventBus.emit('home:workspace-change');
  };

  const renderTabContent = useCallback(() => {
    switch (selectedTab) {
      case 'workspaces':
        return (
          <ManagementDropdownWorkspaceList
            onWorkspaceChanged={onWorkspaceChanged}
            currentWorkspace={currentWorkspace}
          />
        );
      case 'organizations':
        return <ManagementDropdownOrganizationList onCreateNewOrganizationClicked={onCreateNewOrganizationClicked} />;
      case 'referrals':
        return <ManagementDropdownReferralsTab onReferralLinkCopy={onClose} />;
      default:
        return;
    }
  }, [currentWorkspace, onClose, selectedTab]);

  const renderFooter = useCallback(() => {
    if (selectedTab === 'workspaces') {
      return <OrganizationLink to='/workspaces'>Manage Workspaces</OrganizationLink>;
    } else if (selectedTab === 'organizations') {
      return <div onClick={onCreateNewOrganizationClicked}>Create new organization</div>;
    }

    return null;
  }, [selectedTab]);

  const renderUserInitials = useCallback(
    () => (
      <span className='w-10 h-10 flex flex-shrink-0 justify-center items-center bg-bedrock-brand-secondary text-bedrock-brand-text rounded-full'>
        {userInitials}
      </span>
    ),
    [userInitials]
  );

  const renderOrgInitials = useCallback(
    () => (
      <span className='absolute left-[34px] top-0 bedrock-org-logo-ring w-10 h-10 flex flex-shrink-0 justify-center items-center bg-bedrock-brand-primary text-bedrock-brand-text rounded-full'>
        {orgInitials}
      </span>
    ),
    [orgInitials]
  );

  const renderUserAvatar = useCallback(() => {
    if (!user?.avatar_url) return null;

    return (
      <ImageRenderer
        src={user?.avatar_url}
        className='w-10 h-10 flex flex-shrink-0 justify-center items-center rounded-full'
        errorHTML={renderUserInitials()}
      />
    );
  }, [user?.avatar_url, renderUserInitials]);

  const renderOrgIcon = useCallback(() => {
    if (!currentOrganization?.branding?.icon_url) return null;

    return (
      <ImageRenderer
        src={currentOrganization?.branding?.icon_url}
        className='absolute left-[34px] top-0 bedrock-org-logo-ring w-10 h-10 flex flex-shrink-0 justify-center items-center bg-white rounded-full'
        errorHTML={renderOrgInitials()}
      />
    );
  }, [currentOrganization?.branding?.icon_url, renderOrgInitials]);

  return (
    <>
      <JourneyMenu
        openRef={settingsOpenRef}
        closeRef={settingsCloseRef}
        placement='bottom-start'
        offset={[-8, -50]}
        containerClassName='w-[420px]'
        renderOrigin={() => (
          <div
            onClick={handleSettingsClick}
            className='flex flex-row flex-shrink-0 space-x-2 cursor-pointer hover:opacity-80'
          >
            <div className='relative mr-[34px]'>
              {user?.avatar_url ? renderUserAvatar() : renderUserInitials()}
              {currentOrganization?.branding?.icon_url ? renderOrgIcon() : renderOrgInitials()}
            </div>

            <div className='flex flex-col  min-w-fit'>
              <div className='flex flex-row space-x-1 items-center'>
                <span className='text-bedrock-p-strong min-w-fit text-bedrock-black'>
                  {currentWorkspace ? currentWorkspace.name : 'All workspaces'}
                </span>
                <ChevronDownSmallIcon />
              </div>
              <div className='min-w-fit text-bedrock-p-small text-bedrock-black'>
                {currentOrganization?.num_of_users &&
                  getWordWithCount('member', 'members', currentOrganization.num_of_users)}
              </div>
            </div>
          </div>
        )}
        footer={renderFooter()}
      >
        <div className='flex flex-col'>
          <div className='flex flex-row space-x-md divide-x divide-bedrock-gray-medium'>
            <ManagementDropdownTabs activeTab={selectedTab} onChangeTab={onChangeTab} />
            <a
              href='/logout'
              className='flex pl-md items-center text-bedrock-p-strong text-bedrock-black hover:opacity-80'
              rel='noopener noreferrer'
            >
              Sign Out
            </a>
          </div>
          <div className='flex flex-col space-y-sm mt-md'>{renderTabContent()}</div>
        </div>
      </JourneyMenu>
      <CreateOrganizationSlideover
        isCreatingOrganization={isCreatingOrganization}
        handleFormClose={handleCreateOrganizationSlideoverClose}
      />
    </>
  );
};

export default ManagementDropdown;
